import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const MemberManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadMembers();
  }, [page, debouncedSearchTerm]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setPage(1); // Reset to page 1 when searching
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.association_id) {
        setError('Devi essere autenticato come associazione per visualizzare i membri');
        setLoading(false);
        return;
      }

      const response = await axios.get(`/api/associations/${user.association_id}/members`, {
        params: { 
          search: debouncedSearchTerm,
          page: page,
          limit: limit
        }
      });
      
      setMembers(response.data.members || []);
      setTotalMembers(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / limit));
      setLoading(false);
    } catch (err) {
      console.error('Errore nel caricamento dei membri:', err);

      setError('Impossibile caricare i membri. ' + (err.response?.data?.message || 'Verifica la connessione e riprova.'));
      setMembers([]);
      setLoading(false);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container className="mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => navigate('/association')}
        className="mb-4"
      >
        &larr; Torna alla dashboard
      </Button>      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: "#006e39", color: "white" }}>
          <div className="d-flex justify-content-between align-items-center">
            <h4>Ricerca membri dell'associazione</h4>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

          <div className="mb-4">
            <InputGroup size="lg">
              <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                <i className="bi bi-search" style={{ color: '#006e39' }}></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Cerca per nome, cognome, email o codice fiscale..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                disabled={!user?.association_id || loading}
                style={{ borderColor: '#006e39' }}
              />
              {searchTerm && (
                <Button
                  variant="outline-danger"
                  onClick={clearSearch}
                  title="Cancella ricerca"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">La ricerca avviene automaticamente mentre digiti (ricerca server-side)</small>
              <div className="text-muted">
                <i className="bi bi-funnel me-2"></i>
                {totalMembers} membri trovati
              </div>
            </div>
          </div>

          {!user?.association_id ? (
            <Alert variant="warning">
              Devi essere autenticato come associazione per gestire i membri.
            </Alert>
          ) : loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status" variant="success">
                <span className="visually-hidden">Caricamento...</span>
              </Spinner>
              <p className="mt-3">Caricamento dei membri dell'associazione...</p>
            </div>
          ) : members.length === 0 ? (
            <Alert variant="info">
              <div className="text-center">
                <div className="mb-3">
                  <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem', color: '#ffc107' }}></i>
                </div>
                <h5>Nessun membro trovato per la tua associazione.</h5>
                <p>Aggiungi il primo membro utilizzando il pulsante "Aggiungi Membro".</p>              </div>
            </Alert>
          ) : (
            <>
              <Table striped hover responsive>
                <thead className="table-success">
                  <tr>
                    <th>Nome</th>
                    <th>Cognome</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    <th>Codice Fiscale</th>
                    <th>Data di Nascita</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr key={member.id}>
                      <td>{member.first_name || 'N/A'}</td>
                      <td>{member.last_name || 'N/A'}</td>
                      <td>{member.email || 'N/A'}</td>
                      <td>{member.phone || 'N/A'}</td>
                      <td>{member.fiscal_code || 'N/A'}</td>
                      <td>{member.birth_date ? new Date(member.birth_date).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3">
                  <Button
                    variant="outline-success"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="me-2"
                  >
                    <i className="bi bi-chevron-left"></i> Precedente
                  </Button>
                  <span className="mx-3">
                    Pagina {page} di {totalPages}
                  </span>
                  <Button
                    variant="outline-success"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="ms-2"
                  >
                    Successivo <i className="bi bi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MemberManagement;
